import { Alert, Button, Modal, Tabs } from "antd";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import {
  getTransactionsWithEventsForSiteInfiniteOptions,
  validateSiteOptions,
} from "../../services/client/@tanstack/react-query.gen";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "../common/Card";
import { Settings } from "../Settings/Settings";
import { ListOptimizationTasks } from "../Tasks/ListOptimizationTasks";
import { ListForecastingTasks } from "../Tasks/ListForecastingTasks";
import { ListPredictionTasks } from "../Tasks/ListPredictionTasks";
import { SiteConfigurationTab } from "./SiteConfig/components/SiteConfigurationTab";
import { MeasurementsArrowChart } from "./Components/MeasurementsArrowChart";
import { ValidationAlert } from "./ValidationTab/ValidationAlert";
import { AssetValidationTable } from "./ValidationTab/AssetValidationTable";
import { siteDetailsTabKeys } from "./siteDetailsKeys";
import { useNotify } from "../util-components/Notifications/notifications";
import { Typography } from "antd";
import {
  TransactionsTable,
  type TransactionFilters,
} from "./TransactionsTable";
import { ProfilesList } from "./Profile/ProfileList";
import { OcppForwardingTab } from "./OcppForwarding/OcppForwardingTab";
import { DriversTab } from "./Drivers/DriversTab";
import { ScheduleFollowingTab } from "./ScheduleFollowing/ScheduleFollowingTab";
import { ControllerTab } from "./Controller/ControllerTab";
import { O11yTab } from "./O11y/O11yTab";
import { FailedMessagesTab } from "./FailedMessages/FailedMessagesTab";
import { OcppMessagesTab } from "./OcppMessages/OcppMessagesTab";
import { SiteIoConfigSection } from "./Controller/SiteIoConfig/SiteIoConfigSection";
import { useState } from "react";
import { CreateProfileForm } from "./Profile/CreateProfileForm";
import { DetailedCharts } from "./SiteCharts/DetailedCharts";
import { ONE_HOUR } from "../../util/time";
import { FETCH_PAGE_SIZE } from "../../hooks/useCursorPagination";
import { isViewAsAdmin } from "../../util/viewAsAdmin";
import { DisableForViewer } from "../common/DisableForViewer";
import { formatDateTime } from "../../util/formatDate";
import { getChargingAssetNames } from "../../util/siteUtils";

const { Text } = Typography;

const { TabPane } = Tabs;

const SiteDetails: React.FC = () => {
  const { site_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [newProfileModalVisible, setNewProfileModalVisible] = useState(false);
  const viewAsAdmin = isViewAsAdmin();
  const [transactionFilters, setTransactionFilters] =
    useState<TransactionFilters>();

  const TWELVE_HOURS_AGO = new Date(Date.now() - 12 * ONE_HOUR);
  const SIX_HOURS_FROM_NOW = new Date(Date.now() + 6 * ONE_HOUR);

  const [from] = useState(TWELVE_HOURS_AGO);
  const [to] = useState(SIX_HOURS_FROM_NOW);

  const tabFromUrl = searchParams.get("tab") || "1";
  const activeTabKey = Object.values(siteDetailsTabKeys).includes(tabFromUrl)
    ? tabFromUrl
    : "1";

  const { isLoading, error, data, refetch } = useQuery({
    ...validateSiteOptions({ path: { site_id: site_id! } }),
    enabled: !!site_id,
  });

  const {
    data: transactionPages,
    error: transactionsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: transactionsLoading,
  } = useInfiniteQuery({
    ...getTransactionsWithEventsForSiteInfiniteOptions({
      path: { site_id: site_id! },
      query: {
        page_size: FETCH_PAGE_SIZE,
        ...transactionFilters,
      },
    }),
    getNextPageParam: (lastPage) => lastPage.end_cursor,
    placeholderData: keepPreviousData,
  });

  if (!site_id) {
    navigate("/404");
    return null;
  }

  const handleTabChange = (key: string) => {
    searchParams.set("tab", key);
    setSearchParams(searchParams);
  };

  const handleRevalidate = async () => {
    try {
      await refetch();
      notify({
        message: "Site validated! Check the table for details.",
        type: "success",
      });
    } catch (_: unknown) {
      notify({
        message: "Failed to validate site",
        type: "error",
      });
    }
  };

  return (
    <Tabs activeKey={activeTabKey} type="card" onChange={handleTabChange}>
      <TabPane tab="Overview" key={siteDetailsTabKeys["Overview"]}>
        <Card title={"Site: " + data?.name}>
          {data?.status === "shadow" && (
            <div className="w-full mb-4">
              <Alert
                className="text-center"
                message="This site is in shadow mode: forecasting and optimization run as usual, but schedules are not sent to the controller."
                type="warning"
              />
            </div>
          )}
          {data && <ValidationAlert site={data} />}
          {data &&
            (data.configurations?.site_configuration ? (
              <DetailedCharts site={data} from={from} to={to} />
            ) : (
              <MeasurementsArrowChart site_id={site_id} />
            ))}
        </Card>
      </TabPane>

      <TabPane tab="Optimizations" key={siteDetailsTabKeys["Optimizations"]}>
        <Card title={`Optimizations - ${site_id}`}>
          <ListOptimizationTasks
            site_id={site_id}
            className="w-full"
            getDetailsUrl={(record) => `/optimizations/${record.id}`}
          />
        </Card>
      </TabPane>

      <TabPane tab="Predictions" key={siteDetailsTabKeys["Predictions"]}>
        <Card title={`Predictions - ${site_id}`}>
          <ListPredictionTasks site_id={site_id} className="w-full" />
        </Card>
      </TabPane>

      <TabPane tab="Forecasters" key={siteDetailsTabKeys["Forecasters"]}>
        <Card title={`Forecasters - ${site_id}`}>
          <ListForecastingTasks site_id={site_id} className="w-full" />
        </Card>
      </TabPane>
      <TabPane tab="Transactions" key={siteDetailsTabKeys["Transactions"]}>
        <Card
          title={`Transactions - ${site_id}`}
          isLoading={isLoading}
          error={error}
        >
          {transactionPages && (
            <TransactionsTable
              siteId={site_id}
              pages={transactionPages.pages}
              loading={transactionsLoading}
              error={!!transactionsError}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onFilterChange={setTransactionFilters}
            />
          )}
        </Card>
      </TabPane>
      <TabPane
        tab="Site Configuration"
        key={siteDetailsTabKeys["Site Configuration"]}
      >
        <Card title="Site Configuration" isLoading={isLoading} error={error}>
          <SiteConfigurationTab
            site_id={site_id}
            site_configuration={data?.configurations?.site_configuration}
            refetch_site={refetch}
          />
        </Card>
      </TabPane>

      <TabPane tab="Validation" key={siteDetailsTabKeys["Validation"]}>
        <Card
          title="Validation"
          isLoading={isLoading}
          error={error}
          extra={
            <div>
              <Text type="secondary">Last time validated on: </Text>
              <Text strong className="mr-2">
                {data?.validation_status?.last_validation
                  ? formatDateTime(data?.validation_status?.last_validation)
                  : "never"}
              </Text>
              <DisableForViewer>
                <Button
                  onClick={() => handleRevalidate()}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {`Validate site`}
                </Button>
              </DisableForViewer>
            </div>
          }
        >
          {data && <AssetValidationTable site={data} isLoading={isLoading} />}
        </Card>
      </TabPane>

      <TabPane tab="Settings" key={siteDetailsTabKeys["Settings"]}>
        <Card
          title={`Settings - ${site_id}`}
          isLoading={isLoading}
          error={error}
        >
          {data && <Settings site={data} refetchSite={refetch} />}
        </Card>
      </TabPane>

      <TabPane tab="Profiles" key={siteDetailsTabKeys["Profiles"]}>
        <Card
          title={`Profiles - ${site_id}`}
          className="mb-6"
          extra={[
            <DisableForViewer key="create-profile">
              <Button
                type="primary"
                data-cy="create-profile-button"
                className="w-full"
                onClick={() => setNewProfileModalVisible(true)}
              >
                Create New Profile
              </Button>
            </DisableForViewer>,
          ]}
        >
          <ProfilesList site_id={site_id} />
        </Card>
        <Modal
          className="w-full"
          open={newProfileModalVisible}
          onCancel={() => setNewProfileModalVisible(false)}
          footer={null}
        >
          <CreateProfileForm
            site_id={site_id}
            onSuccess={() => setNewProfileModalVisible(false)}
          />
        </Modal>
      </TabPane>

      <TabPane tab="Drivers" key={siteDetailsTabKeys["Drivers"]}>
        <Card title={`Drivers - ${site_id}`}>
          <DriversTab site_id={site_id} partner_id={data?.partner_id} />
        </Card>
      </TabPane>

      <TabPane
        tab="Failed Messages"
        key={siteDetailsTabKeys["Failed Messages"]}
      >
        <Card title={`Failed Messages - ${site_id}`}>
          <FailedMessagesTab site_id={site_id} />
        </Card>
      </TabPane>

      <TabPane tab="OCPP Broker" key={siteDetailsTabKeys["OCPP Broker"]}>
        <Card title="OCPP Broker">
          <OcppForwardingTab
            site_id={site_id}
            charging_pole_names={data ? getChargingAssetNames(data) : []}
          />
        </Card>
      </TabPane>

      {viewAsAdmin && (
        <TabPane tab="OCPP Messages" key={siteDetailsTabKeys["OCPP Messages"]}>
          <Card title={`OCPP Messages - ${site_id}`}>
            <OcppMessagesTab site_id={site_id} />
          </Card>
        </TabPane>
      )}

      {viewAsAdmin && (
        <TabPane
          tab="Schedule Following"
          key={siteDetailsTabKeys["Schedule Following"]}
        >
          <Card title="Schedule Following" isLoading={isLoading} error={error}>
            {data && <ScheduleFollowingTab site={data} />}
          </Card>
        </TabPane>
      )}

      {viewAsAdmin && (
        <TabPane tab="Controller" key={siteDetailsTabKeys["Controller"]}>
          <Card title="Controller">
            <ControllerTab site_id={site_id} />
          </Card>
        </TabPane>
      )}

      {viewAsAdmin && (
        <TabPane
          tab="Site IO Configuration"
          key={siteDetailsTabKeys["Site IO Configuration"]}
        >
          <Card title="Site IO Configuration">
            <SiteIoConfigSection site_id={site_id} />
          </Card>
        </TabPane>
      )}

      {viewAsAdmin && (
        <TabPane tab="o11y" key={siteDetailsTabKeys["O11y"]}>
          <Card title="Observability">
            <O11yTab site_id={site_id} />
          </Card>
        </TabPane>
      )}
    </Tabs>
  );
};

export default SiteDetails;
